/**
 * ── A PHOTO THE CDN CANNOT SERVE NEVER REACHES THE FILL (2026-09-09) ────────────
 *
 * Litmus, "Open for Tours" Nashville, 44 clients: the Ashbury card image failed in
 * every one of them, and in the plain browser preview too. Measured against the live
 * feed the same day: Classic Collection's Ashbury row carries a `hero` AND an
 * `elevation` that both 404 on cdn.lennar.com (a stale contentassets id) — only its
 * `kitchen` loads. Ravens Crest's Ashbury, same plan number, loads all three. The
 * fill takes `hero || elevation || kitchen`, the first NON-EMPTY string, so it shipped
 * the dead hero, and 44 renders of a broken-image box followed. Nothing in the app
 * ever asked whether the URL it was about to put in a customer's inbox loads.
 *
 * So the pack is scrubbed once, at fetch, before any fill sees it: every image URL a
 * card can pick from is probed with an `Image()`, and a URL that DEFINITIVELY fails
 * (onerror) is blanked. The fill's own precedence then does the right thing on its
 * own — hero dead → elevation; both dead → kitchen; all dead → the existing borrow
 * (a home built to the plan, then the community hero). Nothing new to teach it.
 *
 * Rules, each deliberate:
 *   · ONLY A DEFINITE FAILURE BLANKS. A timeout, a jsdom/no-DOM environment, or a
 *     probe that never settles leaves the URL alone. Blanking on uncertainty would
 *     strip good photos on a slow network, which is worse than the bug.
 *   · PROBE ONLY WHAT WOULD BE USED. Per plan the candidates are tried IN FILL ORDER
 *     and the walk stops at the first one that loads — one request per plan in the
 *     common case, not three. Plans probe in parallel.
 *   · THE BROWSER KEEPS THE BYTES. The same URL renders in the preview a moment
 *     later, so a successful probe is not a wasted request; it is a pre-load.
 *   · RESULTS ARE REMEMBERED for the session, so the wizard, Add by ID and a refetch
 *     of the same pack do not re-probe a URL already known dead or alive.
 *   · Cross-origin safe: `onload`/`onerror` fire on any image; no canvas, no CORS.
 */
import type { CommunityPackResponse, HomesiteInfo, PlanInfo } from "../types/api";

/** how long one probe may take before it is treated as "unknown, keep it" */
export const IMG_PROBE_MS = 4000;

export type ImgVerdict = "alive" | "dead" | "unknown";
const memo = new Map<string, Promise<ImgVerdict>>();

/** for tests and for a fresh session */
export function resetImgProbeCache(): void { memo.clear(); }

/* jsdom has an `Image` that never loads anything, so every probe there would sit
   on the timeout and every pack fetch in the suite would take IMG_PROBE_MS. Probing
   is off under jsdom unless a test turns it on with its own Image stand-in. */
let forced: boolean | null = null;
export function setImgProbeEnabled(on: boolean | null): void { forced = on; }
const hasDom = (): boolean => {
  if (forced !== null) return forced;
  if (typeof window === "undefined" || typeof (window as unknown as { Image?: unknown }).Image !== "function") return false;
  return !/jsdom/i.test(String(typeof navigator !== "undefined" ? navigator.userAgent : ""));
};

/**
 * Does this URL load as an image? Resolves "alive", "dead" (onerror fired) or
 * "unknown" (no DOM, timeout). Memoised per URL for the life of the page.
 */
export function probeImg(url: string, timeoutMs: number = IMG_PROBE_MS): Promise<ImgVerdict> {
  const u = String(url || "").trim();
  if (!u) return Promise.resolve("dead");
  if (!hasDom()) return Promise.resolve("unknown");
  const hit = memo.get(u);
  if (hit) return hit;
  const p = new Promise<ImgVerdict>((res) => {
    let done = false;
    const fin = (v: ImgVerdict) => { if (!done) { done = true; res(v); } };
    const im = new Image();
    im.onload = () => fin(im.naturalWidth > 0 ? "alive" : "dead");
    im.onerror = () => fin("dead");
    setTimeout(() => fin("unknown"), timeoutMs);
    try { im.src = u; } catch { fin("dead"); }
  });
  memo.set(u, p);
  return p;
}

/**
 * Walk candidate URLs in the caller's precedence; blank each one that is dead until
 * one is alive or unknown. Returns the list in the same order. Empty strings pass
 * through (they were never candidates).
 */
export async function firstLive(urls: string[], timeoutMs: number = IMG_PROBE_MS): Promise<string[]> {
  const out = urls.slice();
  for (let i = 0; i < out.length; i++) {
    const u = String(out[i] || "");
    if (!u) continue;
    const v = await probeImg(u, timeoutMs);
    if (v === "dead") { out[i] = ""; continue; }
    break; /* alive or unknown: this is what the fill will use — stop probing */
  }
  return out;
}

/** the plan-card precedence, exactly as echDyn resolves `plan.self.image` */
export async function scrubPlan(pl: PlanInfo, timeoutMs: number = IMG_PROBE_MS): Promise<PlanInfo> {
  const [hero, elevation, kitchen] = await firstLive([pl.hero || "", pl.elevation || "", pl.kitchen || ""], timeoutMs);
  if (hero === (pl.hero || "") && elevation === (pl.elevation || "") && kitchen === (pl.kitchen || "")) return pl;
  return { ...pl, hero, elevation, kitchen };
}

/** a home has one photo; dead → "" so the plan/community borrow takes over */
export async function scrubHome(h: HomesiteInfo, timeoutMs: number = IMG_PROBE_MS): Promise<HomesiteInfo> {
  const img = String((h as HomesiteInfo & { img?: string }).img || "");
  if (!img) return h;
  const [live] = await firstLive([img], timeoutMs);
  return live === img ? h : ({ ...h, img: live } as HomesiteInfo);
}

/**
 * Scrub a whole community pack: plans, homes and the community hero, in parallel.
 * Never throws; a pack that cannot be probed is returned as-is.
 */
export async function scrubPackImages<T extends CommunityPackResponse>(C: T, timeoutMs: number = IMG_PROBE_MS): Promise<T> {
  if (!C || !hasDom()) return C;
  try {
    const plans = Array.isArray(C.plans) ? await Promise.all((C.plans as PlanInfo[]).map((p) => scrubPlan(p, timeoutMs))) : C.plans;
    const homes = Array.isArray(C.homesites) ? await Promise.all((C.homesites as HomesiteInfo[]).map((h) => scrubHome(h, timeoutMs))) : C.homesites;
    let community = C.community;
    const ch = community ? String(community.hero || "") : "";
    if (community && ch) {
      const [hero] = await firstLive([ch], timeoutMs);
      if (hero !== ch) community = { ...community, hero };
    }
    return { ...C, plans, homesites: homes, community };
  } catch {
    return C;
  }
}
